import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import Badge from '../../components/ui/Badge';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { deleteCrewFile } from '../../services/mutations';
import { crewDisplayName } from './formMappers';
import type { CrewListItem } from './types';

export function ageFromBirthDate(birthDate?: string | null): string {
  if (!birthDate) return '';
  const d = new Date(birthDate);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age >= 0 ? String(age) : '';
}

export function formatWage(row: CrewListItem): string {
  const tx = row.currentTransaction;
  if (tx?.totalWage == null || tx.totalWage === '') return '';
  const amount = typeof tx.totalWage === 'number' ? tx.totalWage : Number(tx.totalWage);
  if (!Number.isFinite(amount)) return String(tx.totalWage);
  const formatted = amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return tx.wageCurrency ? `${formatted} ${tx.wageCurrency}` : formatted;
}

export const moduleConfig = {
  module: {
    title: 'Crew Pool',
    permissionModuleKey: 'crew',
    route: {
      list: '/crew',
      new: '/crew/new',
      detail: (id: number) => `/crew/${id}`,
      file: (id: number) => `/crew/${id}/file`,
      filePrint: (id: number) => `/crew/${id}/file/print`,
      imoCrewListPrint: '/crew/list/print/imo',
      crewListPrint: (key: string) => `/crew/list/print/${key}`,
      replacementPlanPrint: '/crew/replacement-planner/print',
      crewChangesCostPrint: '/crew/crew-changes-cost/print',
      reliefBoardPrint: '/crew/relief-board/print',
      coeWatchlistPrint: '/crew/coe-watchlist/print',
      certGapsPrint: '/crew/cert-gaps/print',
      rankRequirementsPrint: '/crew/certificate-requirements/print',
      rankRequirementsByNationalityPrint: '/crew/certificate-requirements/print/by-nationality',
      edit: (id: number) => `/crew/${id}/edit`},
    api: {
      list: '/crew',
      detail: (id: number) => `/crew/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by name, ref, phone or email…' },
  filterGroups: [
    {
      name: 'identity',
      label: 'Identity',
      icon: '',
      fields: ['rank', 'nationality', 'notRecommended']},
    {
      name: 'availability',
      label: 'Availability',
      icon: '',
      fields: ['availabilityStatus', 'availableFrom', 'manningAgent', 'transactionStatus', 'vesselId']},
  ],
  filters: {
    fields: [
      {
        key: 'rank',
        label: 'Rank',
        placeholder: 'All ranks',
        getValue: (item: CrewListItem) => item.rank?.name || '',
        options: [],
        apiEndpoint: '/crew-lookups/ranks',
        labelField: 'name'},
      {
        key: 'nationality',
        label: 'Nationality',
        placeholder: 'All nationalities',
        getValue: (item: CrewListItem) => item.nationality?.name || '',
        options: [],
        apiEndpoint: '/crew-lookups/nationalities',
        labelField: 'name'},
      {
        key: 'availabilityStatus',
        label: 'Av. status',
        placeholder: 'All statuses',
        getValue: (item: CrewListItem) => item.availabilityStatus?.name || '',
        options: [],
        apiEndpoint: '/crew-lookups/availability-statuses',
        labelField: 'name'},
      {
        key: 'manningAgent',
        label: 'Manning agent',
        placeholder: 'All agents',
        getValue: (item: CrewListItem) => item.manningAgent?.name || '',
        options: [],
        apiEndpoint: '/crew-lookups/manning-agents',
        labelField: 'name'},
      {
        key: 'transactionStatus',
        label: 'ONB/OFB',
        placeholder: 'All',
        getValue: (item: CrewListItem) => item.currentTransaction?.status || '',
        options: [
          { value: 'ONB', label: 'ONB' },
          { value: 'OFB', label: 'OFB' },
        ]},
      {
        key: 'vesselId',
        label: 'Vessel',
        placeholder: 'All vessels',
        getValue: (item: CrewListItem) => item.currentTransaction?.vesselName || '',
        options: [],
        apiEndpoint: '/vessels',
        labelField: 'vesselName',
        valueField: 'id'},
      {
        key: 'notRecommended',
        label: 'Not recommended',
        type: 'boolean' as const,
        getValue: (item: CrewListItem) => (item.notRecommended ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ]},
      {
        key: 'availableFrom',
        label: 'Av. from',
        type: 'date' as const,
        getValue: (item: CrewListItem) => item.availableFrom || '',
        options: []},
    ],
    applyFilter: (items: CrewListItem[]) => items,
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New crew file',
        showTitle: false,
        variant: 'primary' as const,
        href: '/crew/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: CrewListItem) => item.id,
  },
  emptyStates: {
    noData: 'Create a crew file to manage seafarer records.',
    noSearchResults: 'No crew files match your search.',
    noFilterResults: 'No crew files match the filters.',
  },
  delete: {
    title: 'Delete crew file',
    message: 'Delete this crew file and all related records?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete crew file.',
    handler: async (id: number) => {
      await deleteCrewFile(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  detail: {
    subtitle: 'Crew dashboard',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This crew file does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Crew file',
    getTitle: (item: CrewListItem) => crewDisplayName(item)},
  file: {
    title: 'Crew file',
    subtitle: 'Full record'},
  form: {
    new: { title: 'New crew file', subtitle: 'Create seafarer record' },
    edit: { title: 'Edit crew file', submitText: 'Save' },
    cancelText: 'Cancel'},
  /** Custom tabbed form (Form.tsx); GenericListPage only needs list config. */
  fields: [] as FieldConfig[]};

const TextCell: React.FC<{ value: string | null | undefined; className?: string }> = ({
  value,
  className}) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm whitespace-nowrap', colors.text, className)}>{value || ''}</span>;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<CrewListItem>[] => [
  {
    key: 'lastName',
    header: 'Name',
    sortable: true,
    render: (_v, row) => {
      const name = [row.lastName, row.firstName].filter((p) => p?.trim()).join(' ');
      return <TextCell value={name} />;
    }},
  {
    key: 'age',
    header: 'Age',
    sortable: false,
    render: (_v, row) => <TextCell value={ageFromBirthDate(row.birthDate)} />},
  {
    key: 'rank',
    header: 'Lic Rank',
    sortable: false,
    render: (_v, row) => <TextCell value={row.rank?.abrv || row.rank?.name} />},
  {
    key: 'nationality',
    header: 'Nationality',
    sortable: false,
    render: (_v, row) => <TextCell value={row.nationality?.name} />},
  {
    key: 'birthDate',
    header: 'Birthdate',
    sortable: true,
    render: (v) => <TextCell value={formatDate(v)} />},
  {
    key: 'availableFrom',
    header: 'Av. From',
    sortable: true,
    render: (v) => <TextCell value={formatDate(v)} />},
  {
    key: 'availableTo',
    header: 'Av. to',
    sortable: true,
    render: (v) => <TextCell value={formatDate(v)} />},
  {
    key: 'availabilityStatus',
    header: 'Av. Status',
    sortable: false,
    render: (_v, row) => <TextCell value={row.availabilityStatus?.name} />},
  {
    key: 'lastRank',
    header: 'Last Rank',
    sortable: false,
    render: (_v, row) => (
      <TextCell value={row.currentTransaction?.rank?.abrv || row.currentTransaction?.rank?.name} />
    )},
  {
    key: 'vessel',
    header: 'Vessel',
    sortable: false,
    render: (_v, row) => <TextCell value={row.currentTransaction?.vesselName} />},
  {
    key: 'transactionStatus',
    header: 'Status',
    sortable: false,
    render: (_v, row) => {
      const status = row.currentTransaction?.status;
      if (!status) return <TextCell value="" />;
      return (
        <Badge
          variant={status === 'ONB' ? 'success' : 'default'}
          className="shrink-0 border rounded-md px-2 py-0.5"
        >
          {status}
        </Badge>
      );
    }},
  {
    key: 'signOnDate',
    header: 'S/On Date',
    sortable: false,
    render: (_v, row) => <TextCell value={formatDate(row.currentTransaction?.signOnDate ?? undefined)} />},
  {
    key: 'signOffDate',
    header: 'S/Off Date',
    sortable: false,
    render: (_v, row) => <TextCell value={formatDate(row.currentTransaction?.signOffDate ?? undefined)} />},
  {
    key: 'coeDate',
    header: 'COE',
    sortable: false,
    render: (_v, row) => <TextCell value={formatDate(row.currentTransaction?.coeDate ?? undefined)} />},
  {
    key: 'wage',
    header: 'Wage',
    sortable: false,
    render: (_v, row) => <TextCell value={formatWage(row)} />},
  {
    key: 'telephone1',
    header: 'Phone',
    sortable: false,
    render: (_v, row) => {
      const phones = [row.telephone1 || row.mobile1, row.telephone2 || row.mobile2]
        .map((p) => p?.trim())
        .filter(Boolean);
      return <TextCell value={phones.join(' / ')} />;
    }},
  {
    key: 'manningAgent',
    header: 'Manning Agent',
    sortable: false,
    render: (_v, row) => <TextCell value={row.manningAgent?.name} />},
  {
    key: 'overallEvaluation',
    header: 'Overall Evaluation',
    sortable: false,
    render: (v) => <TextCell value={v} />},
  {
    key: 'lastApplicationDate',
    header: 'Last Appl',
    sortable: true,
    render: (v) => <TextCell value={formatDate(v)} />},
  {
    key: 'lastContactedDate',
    header: 'Last Cntr',
    sortable: true,
    render: (v) => <TextCell value={formatDate(v)} />},
];

const TableActionsCell: React.FC<{
  item: CrewListItem;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => {
  const { colors, theme } = useTheme();
  const { user } = useAuth();
  const agencyMode = String(user?.typeLocation ?? '').toUpperCase() === 'MANNING_AGENT';
  const isDark = theme === 'dark';
  const viewPath = agencyMode
    ? moduleConfig.module.route.file(item.id)
    : moduleConfig.module.route.detail(item.id);
  return (
  <div className="flex items-center flex-nowrap">
    <Link
      to={viewPath}
      className={cn('p-1 rounded-lg transition-colors', colors.primaryFg, colors.interactiveHover)}
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        className={cn('p-1 rounded-lg transition-colors', colors.primaryFg, colors.interactiveHover)}
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </Link>
    )}
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className={cn(
          'p-1 rounded-lg transition-colors',
          isDark ? 'text-red-400 hover:bg-red-950/40' : 'text-red-600 hover:bg-red-50',
        )}
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    )}
  </div>
  );
};

export const getTableActions = (
  item: CrewListItem,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getStatusBadges = () => null;
